/** Zustand global app store: tabs, sidebar/panel collapse, theme, locale */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { isThemePreference, resolveTheme, type ResolvedTheme, type ThemePreference } from '../lib/theme'
import { detectBrowserLocale, isLocale, type Locale } from '../i18n/locale'

export interface UploadQueueItem {
  id: string
  file: File
  progress: number // 0-100
  status: 'queued' | 'uploading' | 'complete' | 'error'
  error?: string
}

export interface Tab {
  id: string
  documentId: string
  title: string
  isDirty: boolean
}

interface AppState {
  // Tabs
  openTabs: Tab[]
  activeTabId: string | null
  openTab: (tab: Omit<Tab, 'isDirty'>) => void
  closeTab: (tabId: string) => void
  setActiveTab: (tabId: string) => void
  markTabDirty: (tabId: string, dirty: boolean) => void
  updateTabTitle: (tabId: string, title: string) => void

  // Panel visibility
  sidebarCollapsed: boolean
  metadataPanelCollapsed: boolean
  setSidebarCollapsed: (collapsed: boolean) => void
  setMetadataPanelCollapsed: (collapsed: boolean) => void

  // Mobile drawer state
  mobileSidebarOpen: boolean
  mobileMetadataOpen: boolean
  setMobileSidebarOpen: (open: boolean) => void
  setMobileMetadataOpen: (open: boolean) => void

  // Command palette
  commandPaletteOpen: boolean
  setCommandPaletteOpen: (open: boolean) => void
  toggleCommandPalette: () => void

  // Create-project dialog (opened from the shell sidebar and the command palette)
  createProjectOpen: boolean
  setCreateProjectOpen: (open: boolean) => void

  // Storage drawer
  storageDrawerOpen: boolean
  setStorageDrawerOpen: (open: boolean) => void
  toggleStorageDrawer: () => void

  // Upload queue (for progress tracking)
  uploadQueue: UploadQueueItem[]
  addToUploadQueue: (items: Array<{ id: string; file: File }>) => void
  updateUploadProgress: (id: string, progress: number) => void
  updateUploadStatus: (id: string, status: UploadQueueItem['status'], error?: string) => void
  removeFromUploadQueue: (id: string) => void

  // Folder expanded state (persisted across refreshes)
  expandedFolderIds: string[]
  toggleFolderExpanded: (folderId: string) => void
  setFolderExpanded: (folderId: string, expanded: boolean) => void

  // Theme: the user's choice plus the resolved value that components render with
  themePreference: ThemePreference
  theme: ResolvedTheme
  setThemePreference: (preference: ThemePreference) => void
  /** Flip between explicit light and dark (legacy sidebar toggle) */
  toggleTheme: () => void
  /** Re-resolve a `system` preference after the OS color scheme changed */
  refreshSystemTheme: () => void

  // Locale
  locale: Locale
  setLocale: (locale: Locale) => void

  // Project the user opened last (synced to the `last_project` preference); /p and non-project pages resume it
  lastProjectSlug: string | null
  setLastProjectSlug: (slug: string | null) => void
}

/** Shape written to localStorage (kept for first paint; the DB value wins once loaded) */
interface PersistedAppState {
  sidebarCollapsed?: boolean
  metadataPanelCollapsed?: boolean
  expandedFolderIds?: string[]
  openTabs?: Omit<Tab, 'isDirty'>[]
  activeTabId?: string | null
  themePreference?: ThemePreference
  theme?: ResolvedTheme
  locale?: Locale
  lastProjectSlug?: string | null
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Tabs
      openTabs: [],
      activeTabId: null,

      openTab: (tab) => {
        const { openTabs } = get()
        const existing = openTabs.find((t) => t.documentId === tab.documentId)
        if (existing) {
          set({ activeTabId: existing.id })
          return
        }
        set({
          openTabs: [...openTabs, { ...tab, isDirty: false }],
          activeTabId: tab.id,
        })
      },

      closeTab: (tabId) => {
        const { openTabs, activeTabId } = get()
        const idx = openTabs.findIndex((t) => t.id === tabId)
        const filtered = openTabs.filter((t) => t.id !== tabId)
        let nextActive = activeTabId
        if (activeTabId === tabId) {
          // Switch to adjacent tab
          if (filtered.length === 0) {
            nextActive = null
          } else if (idx > 0) {
            nextActive = filtered[idx - 1].id
          } else {
            nextActive = filtered[0].id
          }
        }
        set({ openTabs: filtered, activeTabId: nextActive })
      },

      setActiveTab: (tabId) => set({ activeTabId: tabId }),

      markTabDirty: (tabId, dirty) =>
        set((s) => ({
          openTabs: s.openTabs.map((t) => (t.id === tabId ? { ...t, isDirty: dirty } : t)),
        })),

      updateTabTitle: (tabId, title) =>
        set((s) => ({
          openTabs: s.openTabs.map((t) => (t.id === tabId ? { ...t, title } : t)),
        })),

      // Panels
      sidebarCollapsed: false,
      metadataPanelCollapsed: false,
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      setMetadataPanelCollapsed: (collapsed) => set({ metadataPanelCollapsed: collapsed }),

      // Mobile drawers
      mobileSidebarOpen: false,
      mobileMetadataOpen: false,
      setMobileSidebarOpen: (open) => set({ mobileSidebarOpen: open, mobileMetadataOpen: false }),
      setMobileMetadataOpen: (open) => set({ mobileMetadataOpen: open, mobileSidebarOpen: false }),

      // Command palette
      commandPaletteOpen: false,
      setCommandPaletteOpen: (open) => set({ commandPaletteOpen: open }),
      toggleCommandPalette: () => set((s) => ({ commandPaletteOpen: !s.commandPaletteOpen })),

      // Create-project dialog
      createProjectOpen: false,
      setCreateProjectOpen: (open) => set({ createProjectOpen: open }),

      // Storage drawer
      storageDrawerOpen: false,
      setStorageDrawerOpen: (open) => set({ storageDrawerOpen: open }),
      toggleStorageDrawer: () => set((s) => ({ storageDrawerOpen: !s.storageDrawerOpen })),

      // Upload queue
      uploadQueue: [],
      addToUploadQueue: (items) => set((s) => ({
        uploadQueue: [
          ...s.uploadQueue,
          ...items.map(({ id, file }) => ({
            id,
            file,
            progress: 0,
            status: 'queued' as const,
          })),
        ],
      })),
      updateUploadProgress: (id, progress) => set((s) => ({
        uploadQueue: s.uploadQueue.map((u) => u.id === id ? { ...u, progress } : u),
      })),
      updateUploadStatus: (id, status, error) => set((s) => ({
        uploadQueue: s.uploadQueue.map((u) => u.id === id ? { ...u, status, error } : u),
      })),
      removeFromUploadQueue: (id) => set((s) => ({
        uploadQueue: s.uploadQueue.filter((u) => u.id !== id),
      })),

      // Folder expanded state
      expandedFolderIds: [],
      toggleFolderExpanded: (folderId) =>
        set((s) => ({
          expandedFolderIds: s.expandedFolderIds.includes(folderId)
            ? s.expandedFolderIds.filter((id) => id !== folderId)
            : [...s.expandedFolderIds, folderId],
        })),
      setFolderExpanded: (folderId, expanded) =>
        set((s) => ({
          expandedFolderIds: expanded
            ? s.expandedFolderIds.includes(folderId)
              ? s.expandedFolderIds
              : [...s.expandedFolderIds, folderId]
            : s.expandedFolderIds.filter((id) => id !== folderId),
        })),

      // Theme
      themePreference: 'system',
      theme: resolveTheme('system'),
      setThemePreference: (preference) => set({ themePreference: preference, theme: resolveTheme(preference) }),
      toggleTheme: () => {
        const next = get().theme === 'dark' ? 'light' : 'dark'
        set({ themePreference: next, theme: next })
      },
      refreshSystemTheme: () => {
        const { themePreference, theme } = get()
        const resolved = resolveTheme(themePreference)
        if (resolved !== theme) set({ theme: resolved })
      },

      // Locale
      locale: detectBrowserLocale(),
      setLocale: (locale) => set({ locale }),

      // Last opened project
      lastProjectSlug: null,
      setLastProjectSlug: (slug) => {
        if (get().lastProjectSlug !== slug) set({ lastProjectSlug: slug })
      },
    }),
    {
      name: 'agentwiki-app',
      version: 1,
      // v0 stored only the resolved `theme`; treat it as an explicit preference
      migrate: (persisted, version) => {
        const state = (persisted ?? {}) as PersistedAppState
        if (version < 1 && !state.themePreference && (state.theme === 'dark' || state.theme === 'light')) {
          return { ...state, themePreference: state.theme }
        }
        return state
      },
      // Validate persisted values and re-resolve the theme (the OS scheme may have changed since last visit)
      merge: (persisted, current) => {
        const state = (persisted ?? {}) as PersistedAppState
        const themePreference = isThemePreference(state.themePreference) ? state.themePreference : current.themePreference
        return {
          ...current,
          ...state,
          themePreference,
          theme: resolveTheme(themePreference),
          locale: isLocale(state.locale) ? state.locale : current.locale,
          lastProjectSlug: typeof state.lastProjectSlug === 'string' ? state.lastProjectSlug : current.lastProjectSlug,
          openTabs: Array.isArray(state.openTabs)
            ? state.openTabs.map((t) => ({ ...t, isDirty: false }))
            : current.openTabs,
        }
      },
      partialize: (s) => ({
        sidebarCollapsed: s.sidebarCollapsed,
        metadataPanelCollapsed: s.metadataPanelCollapsed,
        expandedFolderIds: s.expandedFolderIds,
        openTabs: s.openTabs.map(({ isDirty: _, ...rest }) => rest),
        activeTabId: s.activeTabId,
        themePreference: s.themePreference,
        theme: s.theme,
        locale: s.locale,
        lastProjectSlug: s.lastProjectSlug,
      }),
    },
  ),
)
